import { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import resolveConfig from 'tailwindcss/resolveConfig';
import { Pagination } from '@aws-amplify/ui-react';
import { chunk } from 'lodash';
import { SpinnerTitle } from './spinner.title';

// config
const MAX_TABLE_ROW = 15;

const MediaSM = ({ children }) => {
  const isDesktop = useMediaQuery({ maxWidth: 384 });
  return isDesktop ? children : null;
}
const MediaMD = ({ children }) => {
  const isDesktop = useMediaQuery({ minWidth: 384 + 1, maxWidth: 448 });
  return isDesktop ? children : null;
}
const MediaLG = ({ children }) => {
  const isDesktop = useMediaQuery({ minWidth: 448 + 1, maxWidth: 512 });
  return isDesktop ? children : null;
}
const MediaXL = ({ children }) => {
  const isDesktop = useMediaQuery({ minWidth: 512 + 1, maxWidth: 576 });
  return isDesktop ? children : null;
}
const Media2XL = ({ children }) => {
  const isDesktop = useMediaQuery({ minWidth: 576 + 1 });
  return isDesktop ? children : null;
}

export const Table = (props) =>  {

  // input
  const { 
    data, 
    hasMorePages = true, 
    columnDefs, 
    loading = false, 
    noDataFallback = null,
    onLoadMore = () => {}
  } = props;

  // state
  const [state, setState] = useState({
    pages: [],
    currentPage: -1
  })

  // columns
  const sm = columnDefs.sm || columnDefs.default;
  const md = columnDefs.md || columnDefs.default;
  const lg = columnDefs.lg || columnDefs.default;
  const xl = columnDefs.xl || columnDefs.default;
  const xxl = columnDefs.xxl || columnDefs.default;

  useEffect(() => {
    const chunked = chunk(data, MAX_TABLE_ROW);
    setState({
      ...state,
      pages: [...chunked],
      currentPage: (state.currentPage === -1 ? 1 : state.currentPage),
    });
  }, [data]);

  useEffect(() => {
    console.log('components.commons.table@useEffect[state]', state)
  }, [state]);

  return (
    <div className="sm:-mx-0 bg-slate-100">
      <div className='mt-2 py-4 flex justify-end items-end'>
        <Pagination
            currentPage={state.currentPage}
            totalPages={state.pages.length}
            siblingCount={3}
            hasMorePages={hasMorePages}
            onPrevious={() => {
              if (state.currentPage > 0) {
                setState({...state, currentPage: state.currentPage - 1});
              }
            }}
            onNext={() => {
              if (state.currentPage < state.pages.length) {
                setState({...state, currentPage: state.currentPage + 1});
              } else {
                onLoadMore();
              }
            }}
            onChange={(value) => {
              setState({
                ...state,
                currentPage: value,
              });
            }}
        />
      </div>
      <table className="min-w-full rounded-md">
        <thead className='border-y border-slate-200 bg-slate-100'>
          <MediaSM>
            <tr>
              {sm.map((col, index) => (
                <th key={index} className={`${col.style}`}>{col.name}</th>
              ))}
            </tr>
          </MediaSM>
          <MediaMD>
          <tr>
              {md.map((col, index) => (
                <th key={index} className={`${col.style}`}>{col.name}</th>
              ))}
            </tr>
          </MediaMD>
          <MediaLG>
          <tr>
              {lg.map((col, index) => (
                <th key={index} className={`${col.style}`}>{col.name}</th>
              ))}
            </tr>
          </MediaLG>
          <MediaXL>
          <tr>
              {xl.map((col, index) => (
                <th key={index} className={`${col.style}`}>{col.name}</th>
              ))}
            </tr>
          </MediaXL>
          <Media2XL>
          <tr>
              {xxl.map((col, index) => (
                <th key={index} className={`${col.style}`}>{col.name}</th>
              ))}
            </tr>
          </Media2XL>
        </thead>

        {/* show when there is data */}
        {data.length > 0 && state.pages[state.currentPage - 1] ?
          <tbody className="bg-white">
            <MediaSM>
              {state.pages[state.currentPage - 1].map((_row, _rowIndex) => (
                <tr id={`row-${_rowIndex}`} key={`row-${_rowIndex}`}>
                  {sm.map((_column, _colIndex) => (
                    _column.row ? _column.row(_row) : <td key={`cell-${_rowIndex}-${_colIndex}`}>{_row[_column.accessorKey]}</td>
                  ))}
                </tr>
              ))}
            </MediaSM>
            <MediaMD>
              {state.pages[state.currentPage - 1].map((_row, _rowIndex) => (
                <tr id={`row-${_rowIndex}`} key={`row-${_rowIndex}`}>
                  {md.map((_column, _colIndex) => (
                    _column.row ? _column.row(_row) : <td key={`cell-${_rowIndex}-${_colIndex}`}>{_row[_column.accessorKey]}</td>
                  ))}
                </tr>
              ))}
            </MediaMD>
            <MediaLG>
              {state.pages[state.currentPage - 1].map((_row, _rowIndex) => (
                <tr id={`row-${_rowIndex}`} key={`row-${_rowIndex}`}>
                  {lg.map((_column, _colIndex) => (
                    _column.row ? _column.row(_row) : <td key={`cell-${_rowIndex}-${_colIndex}`}>{_row[_column.accessorKey]}</td>
                  ))}
                </tr>
              ))}
            </MediaLG>
            <MediaXL>
              {state.pages[state.currentPage - 1].map((_row, _rowIndex) => (
                <tr id={`row-${_rowIndex}`} key={`row-${_rowIndex}`}>
                  {xl.map((_column, _colIndex) => (
                    _column.row ? _column.row(_row) : <td key={`cell-${_rowIndex}-${_colIndex}`}>{_row[_column.accessorKey]}</td>
                  ))}
                </tr>
              ))}
            </MediaXL>
            <Media2XL>
              {state.pages[state.currentPage - 1].map((_row, _rowIndex) => (
                <tr id={`row-${_rowIndex}`} key={`row-${_rowIndex}`}>
                  {xxl.map((_column, _colIndex) => (
                    _column.row ? _column.row(_row) : <td key={`cell-${_rowIndex}-${_colIndex}`}>{_row[_column.accessorKey]}</td>
                  ))}
                </tr>
              ))}
            </Media2XL>
          </tbody>
          :
          null
        }
      </table>

      {/* fallback display when there is no data */}
      {data.length === 0
        ?
        loading
          ?
          <div className='p-5 h-auto flex flex-col items-center justify-between space-y-4 bg-white'>
            <SpinnerTitle spin={loading} />
          </div>
          :
          noDataFallback
            ? noDataFallback
            :
            <div className='p-5 h-auto flex flex-col items-center justify-between space-y-4 bg-white'>
              <p className="text-sm font-bold leading-6 text-gray-500">No data</p>
              <p className="text-sm leading-6 text-gray-500">No data to display.</p>
            </div>
        : null
      }
    </div>
  )
}